import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';

// ─── Types ───────────────────────────────────────────────────────────────────

export type SessionType = 'cash' | 'tournament' | 'multi';
export type GameFormat = 'MTT' | 'SNG' | 'Bounty' | 'AoF' | 'Satellite' | 'Spin' | 'Cash' | 'Other';
export type Platform = 'GGPoker' | 'PokerStars' | 'PartyPoker' | 'WPN' | 'Live' | 'Other';

export interface StakeInfo {
  isStaked: boolean;
  stakerName: string;
  myPercent: number;
}

export interface TournamentEntry {
  id: string;
  name: string;
  format: GameFormat;
  buyIn: number;
  rebuys: number;
  addon: number;
  prize: number;
  bountiesWon: number;
  finishPosition: number | null;
  totalEntrants: number | null;
  notes: string;
}

export interface Session {
  id: string;
  date: string;
  type: SessionType;
  platform: Platform;
  stakes: string;
  buyIn: number;
  cashOut: number;
  tournaments: TournamentEntry[];
  staking: StakeInfo;
  totalInvested: number;
  totalReturned: number;
  grossProfit: number;
  netProfit: number;
  duration: number | null;
  notes: string;
  tags: string[];
}

export type GoalType = 'bankroll' | 'sessions' | 'roi';

export interface Goal {
  id: string;
  type: GoalType;
  description: string;
  targetValue: number;
  deadline: string | null;
  createdAt: string;
  completed: boolean;
}

export interface AppSettings {
  initialBankroll: number;
  currency: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function computeSession(partial: Omit<Session, 'totalInvested' | 'totalReturned' | 'grossProfit' | 'netProfit'>): Session {
  let totalInvested = 0;
  let totalReturned = 0;

  if (partial.type === 'cash') {
    totalInvested = partial.buyIn;
    totalReturned = partial.cashOut;
  } else {
    for (const t of partial.tournaments) {
      totalInvested += t.buyIn + t.rebuys * t.buyIn + t.addon;
      totalReturned += t.prize + t.bountiesWon;
    }
  }

  const grossProfit = totalReturned - totalInvested;
  const netProfit = partial.staking.isStaked
    ? grossProfit * (partial.staking.myPercent / 100)
    : grossProfit;

  return { ...partial, totalInvested, totalReturned, grossProfit, netProfit };
}

// ─── Persistence (localStorage as offline cache) ──────────────────────────────

const STORAGE_KEY = 'grindlog';

function loadState<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    return JSON.parse(raw)[key] ?? fallback;
  } catch { return fallback; }
}

function saveAll(state: { sessions: any; settings: any; goals: any }) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

// ─── Slices ───────────────────────────────────────────────────────────────────

const sessionsSlice = createSlice({
  name: 'sessions',
  initialState: { list: loadState<Session[]>('sessions', []) },
  reducers: {
    loadSessions: (state, action: PayloadAction<Session[]>) => {
      state.list = action.payload;
    },
    addSession: (state, action: PayloadAction<Session>) => {
      // Avoid duplicates on re-import
      if (!state.list.find(s => s.id === action.payload.id)) {
        state.list.unshift(action.payload);
      }
    },
    updateSession: (state, action: PayloadAction<Session>) => {
      const idx = state.list.findIndex(s => s.id === action.payload.id);
      if (idx !== -1) state.list[idx] = action.payload;
    },
    deleteSession: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter(s => s.id !== action.payload);
    },
    resetSessions: (state) => { state.list = []; },
  },
});

const settingsSlice = createSlice({
  name: 'settings',
  initialState: loadState<AppSettings>('settings', { initialBankroll: 0, currency: 'USD' }),
  reducers: {
    updateSettings: (state, action: PayloadAction<Partial<AppSettings>>) => {
      return { ...state, ...action.payload };
    },
  },
});

const goalsSlice = createSlice({
  name: 'goals',
  initialState: { list: loadState<Goal[]>('goals', []) },
  reducers: {
    loadGoals: (state, action: PayloadAction<Goal[]>) => {
      state.list = action.payload;
    },
    addGoal: (state, action: PayloadAction<Goal>) => {
      state.list.push(action.payload);
    },
    updateGoal: (state, action: PayloadAction<Goal>) => {
      const idx = state.list.findIndex(g => g.id === action.payload.id);
      if (idx !== -1) state.list[idx] = action.payload;
    },
    deleteGoal: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter(g => g.id !== action.payload);
    },
    toggleGoal: (state, action: PayloadAction<string>) => {
      const g = state.list.find(g => g.id === action.payload);
      if (g) g.completed = !g.completed;
    },
  },
});

// ─── Store ────────────────────────────────────────────────────────────────────

const store = configureStore({
  reducer: {
    sessions: sessionsSlice.reducer,
    settings: settingsSlice.reducer,
    goals:    goalsSlice.reducer,
  },
});

// Keep localStorage in sync as offline cache
store.subscribe(() => {
  const s = store.getState();
  saveAll({ sessions: s.sessions.list, settings: s.settings, goals: s.goals.list });
});

export const { loadSessions, addSession, updateSession, deleteSession, resetSessions } = sessionsSlice.actions;
export const { updateSettings } = settingsSlice.actions;
export const { loadGoals, addGoal, updateGoal, deleteGoal, toggleGoal } = goalsSlice.actions;

// Thunk: merge remote data into local store (called after login)
export function loadFromRemote(payload: {
  sessions: Session[];
  goals: Goal[];
  settings: Partial<AppSettings> | null;
}) {
  return (dispatch: AppDispatch) => {
    dispatch(loadSessions(payload.sessions));
    dispatch(loadGoals(payload.goals));
    if (payload.settings) {
      dispatch(updateSettings(payload.settings));
    }
  };
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
